// ============================================================================
// 2120, Centralized pricing configuration
// Single source of truth for plans, regions, billing cycles, and amounts.
// Future: this object can be replaced by a server fetch or feature-flagged.
// NOTE: This file is intentionally framework-agnostic so the marketing site
// (separate Next.js / Astro project) can import the same shape without
// dragging in React or app-only deps.
// ============================================================================

export const REGIONS = {
  india: {
    id: 'india',
    label: 'India',
    flag: '\u{1F1EE}\u{1F1F3}',
    currency: 'INR',
    note: 'Pricing in Indian Rupees',
  },
  international: {
    id: 'international',
    label: 'International',
    flag: '\u{1F30D}',
    currency: 'USD',
    note: 'Pricing in US Dollars',
  },
};

export const DEFAULT_REGION = 'india';

// "2 months free" on annual = pay for 10 months, get 12.
// 1 - (10/12) ≈ 16.67% → we round to 17% for display.
export const BILLING_CYCLES = {
  monthly: { id: 'monthly', label: 'Monthly', perLabel: 'month' },
  annual:  { id: 'annual',  label: 'Annual',  perLabel: 'year', savePct: 17, savingsCopy: '2 months free' },
};

// Plan visual symbol identifiers (rendered inline as SVG in Pricing.js)
export const PLAN_SYMBOLS = {
  free:    'spark',
  clarity: 'compass',
  horizon: 'horizon',
  summit:  'summit',
};

// Helper for building annual price from monthly using the "2 months free" rule.
// Keeps the three paid plans visually consistent and makes future price tweaks
// a one-line change.
const annualFromMonthly = (monthly) => ({
  amount: Math.round(monthly * 10 * 100) / 100, // 10× monthly, 2 months free
  monthlyEquiv: Math.round((monthly * 10 / 12) * 100) / 100,
});

// ============================================================================
// PLANS
// Emotional progression:
//   Free     → "I'm curious about my finances."
//   Clarity  → "I understand my finances."
//   Horizon  → "I'm building a stronger financial future."
//   Summit   → "We're building long-term financial stability together."
// ============================================================================
export const PLANS = [
  {
    id: 'clarity',
    name: 'Clarity',
    symbol: 'compass',
    tagline: 'Understand where your money goes.',
    positioning: 'See spending patterns, stay ahead of recurring expenses, and build a clearer relationship with money.',
    description: 'See where your money goes and build a steady, judgment-free relationship with it — one transaction at a time.',
    ctaMonthly: 'Start with Clarity',
    ctaAnnual:  'Start with Clarity',
    ctaCurrent: 'Your current plan',
    highlight: false,
    badge: null,
    accent: 'evergreen',
    // 14-day free trial for Clarity — matches Horizon + Summit cadence.
    trialDays: 14,
    pricing: {
      india: {
        monthly: { amount: 299, currency: 'INR', period: 'month' },
        annual:  { ...annualFromMonthly(299), currency: 'INR', period: 'year' },
      },
      international: {
        monthly: { amount: 7.99, currency: 'USD', period: 'month' },
        annual:  { ...annualFromMonthly(7.99), currency: 'USD', period: 'year' },
      },
    },
    features: [
      'AI-powered financial dashboard',
      'Unlimited transactions',
      'Financial Clarity Score with insights',
      'Monthly reflection',
      'Up to 3 accounts',
      'Up to 3 goals',
      'Up to 5 recurring expenses',
      '12 months of history',
      '4 statement imports per month',
      '25 receipt scans per month',
    ],
    bestFor: 'Individuals who want a clearer picture of their finances.',
    upgradeHint: null,
  },
  {
    id: 'horizon',
    name: 'Horizon',
    symbol: 'horizon',
    tagline: 'See the bigger picture.',
    positioning: 'Track net worth, plan ahead, and understand how today’s decisions shape tomorrow.',
    description: 'A wider lens on your money — investments, projections, and intentional growth, all written kindly.',
    ctaMonthly: 'Start 14-day trial on Horizon',
    ctaAnnual:  'Start 14-day trial on Horizon',
    ctaCurrent: 'Your current plan',
    highlight: true,
    badge: 'Most Popular',
    accent: 'gold',
    // 14-day free trial for Horizon, let users feel the wider lens first.
    trialDays: 14,
    pricing: {
      india: {
        monthly: { amount: 899, currency: 'INR', period: 'month' },
        annual:  { ...annualFromMonthly(899), currency: 'INR', period: 'year' },
      },
      international: {
        monthly: { amount: 14.99, currency: 'USD', period: 'month' },
        annual:  { ...annualFromMonthly(14.99), currency: 'USD', period: 'year' },
      },
    },
    features: [
      'Everything in Clarity',
      'Net Worth Tracker',
      'Financial Independence Tracker',
      'Financial projections (1, 5, 10, and 20 years)',
      'Multi-currency support',
      'Unlimited goals, accounts, and recurring expenses',
      'Full history retention',
      'AI reflections and insights (up to 6 per month)',
      '12 statement imports per month',
    ],
    bestFor: 'People thinking beyond monthly budgeting — investors, builders, and intentional earners.',
    upgradeHint: 'For people thinking beyond the month — investors, builders, and intentional earners.',
  },
  {
    id: 'summit',
    name: 'Summit',
    symbol: 'summit',
    tagline: 'Build your financial future together.',
    positioning: 'Designed for couples and households who want one shared view of their money, goals, and long-term plans.',
    description: 'Two people, one shared picture. Combined net worth, aligned goals, and weekly reflections written for both of you.',
    ctaMonthly: 'Start Building Together',
    ctaAnnual:  'Start Building Together',
    ctaCurrent: 'Your current plan',
    highlight: false,
    badge: null,
    accent: 'evergreen',
    // 14-day free trial on Summit, same window as Horizon, shared with partner.
    trialDays: 14,
    pricing: {
      india: {
        monthly: { amount: 1499, currency: 'INR', period: 'month' },
        annual:  { ...annualFromMonthly(1499), currency: 'INR', period: 'year' },
      },
      international: {
        monthly: { amount: 19.99, currency: 'USD', period: 'month' },
        annual:  { ...annualFromMonthly(19.99), currency: 'USD', period: 'year' },
      },
    },
    features: [
      'Everything in Horizon',
      'Partner/spouse access',
      'Shared financial dashboard',
      'Household net worth',
      'Shared financial goals',
      '25 statement imports per month',
      '200 receipt scans per month',
      'Accountant access (read-only)',
      'Activity and change history',
      'Priority support',
    ],
    bestFor: 'Couples and families managing money together.',
    upgradeHint: null,
  },
];

// ============================================================================
// FREE TIER
// Rendered separately under the three-column hero, quiet, dignified, not
// competing with the paid plans for attention. The conversion thesis is:
// "Use Free, feel the calm, then graduate to Clarity when you want AI + scale."
// ============================================================================
export const FREE_PLAN = {
  id: 'free',
  name: 'Forever Free',
  symbol: 'spark',
  tagline: 'Just begin.',
  positioning: 'A gentle introduction to 2120 — no commitment, no AI, no noise.',
  description: 'Track manually, hold one goal in mind, and see what financial clarity feels like.',
  ctaMonthly: 'Start free',
  ctaAnnual:  'Start free',
  ctaCurrent: 'Your current plan',
  pricing: {
    india: {
      monthly: { amount: 0, currency: 'INR', period: 'month' },
      annual:  { amount: 0, currency: 'INR', period: 'year' },
    },
    international: {
      monthly: { amount: 0, currency: 'USD', period: 'month' },
      annual:  { amount: 0, currency: 'USD', period: 'year' },
    },
  },
  features: [
    'Manual transaction tracking',
    'Last 30 days of history',
    'Basic Clarity Score',
    '1 account, 1 goal, 1 recurring expense',
    '1 PDF statement import (first 7 days)',
    'No AI insights or reflections',
  ],
  bestFor: 'Someone who wants to explore the idea before committing.',
  upgradeHint: 'Move to Clarity when you want AI insights, full history, and more headroom.',
};

export function getPlanPrice(plan, regionId, cycleId = 'monthly') {
  const r = REGIONS[regionId] || REGIONS[DEFAULT_REGION];
  const reg = plan.pricing[r.id] || plan.pricing[DEFAULT_REGION];
  return reg[cycleId] || reg.monthly;
}

export const PILLARS = [
  { title: 'Calm by design',         body: 'No shaming, no aggressive alerts. Every screen designed for a steadier nervous system.' },
  { title: 'Honest with your money', body: 'Original currency preserved. Original amounts preserved. No hidden conversions, just clarity.' },
  { title: 'Future-ready',           body: 'Plans, projections and AI reflections that grow with your life across borders and seasons.' },
];

export const FAQS = [
  { q: 'Can I change plans later?', a: 'Yes. You can move between plans anytime. Upgrades take effect immediately; downgrades take effect at the end of your current billing cycle so you keep the tier you’ve paid for.' },
  { q: 'Do you offer a free trial?', a: 'Yes — all paid plans (Clarity, Horizon, Summit) include a 14-day free trial. No card is charged until the trial ends, and you can cancel anytime in those 14 days at no cost. Free is, of course, free forever.' },
  { q: 'What’s the difference between monthly and annual?', a: 'Monthly is the gentlest commitment. Annual saves you two months — pay for 10, use for 12. You also keep your rate even if pricing changes later.' },
  { q: 'How does 2120 handle privacy?', a: 'Every row of your data is protected by row-level security. Your financial data is encrypted in transit and at rest, and is never shared with third parties for marketing or analytics.' },
  { q: 'Is multi-currency just a forex tool?', a: 'No. 2120 is not a forex platform. Multi-currency exists for one reason — to give people whose lives span countries a single, unified picture of their financial life.' },
  { q: 'What currencies are supported?', a: 'We currently support 70+ currencies, including INR, USD, EUR, GBP, AED, SAR, SGD, CAD, AUD, NZD, JPY, CNY, CHF, ZAR, BRL, MXN, and many more across Europe, the Middle East, Asia-Pacific, Africa, Latin America, and the Caribbean. More on request.' },
  { q: 'Can I get a refund?', a: 'If something goes wrong within your first 7 days on a paid plan, write to us at support@2120.money and we’ll refund you in full — no questions, no friction.' },
  { q: 'Will pricing change later?', a: 'These are introductory rates. Anyone who subscribes now keeps this rate for as long as they keep their subscription active.' },
];
